import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useWalletContext } from '@/contexts/WalletContext'
import { useUser } from '@/hooks/useUser'
import { useStorage } from '@/contexts/StorageContext'
import { useSwipeGesture } from '@/hooks/useSwipeGesture'
import { clearGuestSession } from '@/lib/guestStorage'
import { getSelectedIdentity } from '@/types/UserSettings'
import { normalizeEmojis } from '@/lib/emojiUtils'
import AuthWindow from './AuthWindow'
import ChatApplication from './chat/Chat'
import FileExplorer from './FileExplorer'
import Profile from './Profile'
import Representation from './Representation'
import Settings from './Settings'
import StippledBackground from './StippledBackground'
import MobileNav from './MobileNav'
import CookieConsent from './CookieConsent'
import UsageLimitBanner from './UsageLimitBanner'
import Subscription from './Subscription'
import { MarketplacePurchaseExecutor } from './MarketplacePurchaseExecutor'

const ASSISTANT_NAME = import.meta.env.VITE_ASSISTANT_NAME ?? 'Assistant'

interface MobileWindow {
  id: number
  title: string
  contentType: 'auth' | 'fileExplorer' | 'settings' | 'chat' | 'profile' | 'representation'
  emoji: string
}

export default function MobileDesktop() {
  const { colors, toggleTheme } = useThemeMode()
  const { t, i18n } = useTranslation()
  const { user, isLoading: isLoadingAuth } = useUser()
  const walletContext = useWalletContext()
  const { identities } = useStorage()
  
  const [windows, setWindows] = useState<MobileWindow[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [nextWindowId, setNextWindowId] = useState(1)
  const hasAutoOpenedChat = useRef(false)
  const carouselRef = useRef<HTMLDivElement>(null)
  
  // Font mode state
  const [fontMode, setFontMode] = useState<'departure-mono' | 'open-dyslexic' | 'system'>(() => {
    if (typeof window !== 'undefined') {
      const savedFont = localStorage.getItem('kyogre-font-mode')
      if (savedFont === 'departure-mono' || savedFont === 'open-dyslexic' || savedFont === 'system') {
        return savedFont
      }
    }
    return 'departure-mono'
  })
  
  // Track selected identity
  const [selectedIdentity, setSelectedIdentity] = useState<string>(() => getSelectedIdentity())
  
  // Modal states
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [subscriptionSuccessMessage, setSubscriptionSuccessMessage] = useState<string | null>(null)
  const [showUsageLimitBanner, setShowUsageLimitBanner] = useState(false)
  const [usageLimitResetTime, setUsageLimitResetTime] = useState('')
  
  // Guest mode state
  const [isGuestMode, setIsGuestMode] = useState(false)
  
  // Navigate to previous/next window (simple, no round-robin)
  const goToPrevWindow = useCallback(() => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1)
    }
  }, [activeIndex])
  
  const goToNextWindow = useCallback(() => {
    if (activeIndex < windows.length - 1) {
      setActiveIndex(prev => prev + 1)
    }
  }, [activeIndex, windows.length])
  
  // Swipe gesture handling - uses native event listeners for performance
  // (native listeners allow preventDefault for horizontal swipes)
  const { swipeRef } = useSwipeGesture({
    onSwipeLeft: goToNextWindow,
    onSwipeRight: goToPrevWindow,
    threshold: 60,
    velocityThreshold: 0.35,
  })
  
  // Save font mode
  useEffect(() => {
    localStorage.setItem('kyogre-font-mode', fontMode)
  }, [fontMode])
  
  // Apply font to body
  const fontFamily = useMemo(() => {
    switch (fontMode) {
      case 'departure-mono':
        return 'var(--font-departure-mono), monospace'
      case 'open-dyslexic':
        return 'var(--font-open-dyslexic), sans-serif'
      default:
        return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }
  }, [fontMode])
  
  useEffect(() => {
    document.body.style.fontFamily = fontFamily
    return () => { document.body.style.fontFamily = '' }
  }, [fontFamily])
  
  // Open window function
  const openWindow = useCallback((contentType: MobileWindow['contentType'], emoji: string, title: string) => {
    setWindows(prev => {
      // Check if window already exists
      const existingIndex = prev.findIndex(w => w.contentType === contentType)
      if (existingIndex !== -1) {
        setActiveIndex(existingIndex)
        return prev
      }
      
      const newWindow: MobileWindow = {
        id: nextWindowId,
        title,
        contentType,
        emoji,
      }
      
      // Only increment ID when actually creating a new window
      setNextWindowId(id => id + 1)
      setActiveIndex(prev.length) // Focus new window
      return [...prev, newWindow]
    })
  }, [nextWindowId])
  
  // Close window function
  const closeWindow = useCallback((id: number) => {
    setWindows(prev => {
      const index = prev.findIndex(w => w.id === id)
      
      // Guard against id not found - don't modify state
      if (index === -1) return prev
      
      const newWindows = prev.filter(w => w.id !== id)
      
      setActiveIndex(currentActive => {
        if (currentActive >= index && currentActive > 0) {
          return currentActive - 1
        }
        return currentActive
      })
      
      return newWindows
    })
  }, [])
  
  // Auto-open chat on first load
  useEffect(() => {
    if (!isLoadingAuth && !hasAutoOpenedChat.current) {
      hasAutoOpenedChat.current = true
      const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
      openWindow('chat', '💬', `${t('navigation.chat')}${identityPart}`)
    }
  }, [isLoadingAuth, openWindow, t, i18n.language, selectedIdentity])
  
  // Close all windows when user signs out
  useEffect(() => {
    if (!isLoadingAuth && !user) {
      setWindows([])
      setActiveIndex(0)
    }
  }, [user, isLoadingAuth])
  
  // Listen for identity changes
  useEffect(() => {
    const handleIdentityChange = () => setSelectedIdentity(getSelectedIdentity())
    window.addEventListener('identity-changed', handleIdentityChange)
    return () => window.removeEventListener('identity-changed', handleIdentityChange)
  }, [])
  
  // Update chat window title when identity changes
  useEffect(() => {
    const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
    const newTitle = `${t('navigation.chat')}${identityPart}`
    
    setWindows(prev => prev.map(w => 
      w.contentType === 'chat' ? { ...w, title: newTitle } : w
    ))
  }, [selectedIdentity, t, i18n.language])
  
  // Check for Stripe return URLs
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkoutStatus = params.get('checkout')
    const portalStatus = params.get('portal')
    const sessionId = params.get('session_id')
    
    if (checkoutStatus === 'success') {
      setSubscriptionSuccessMessage('✨ Full Access Granted! Your subscription is now active.')
      setShowSubscriptionModal(true)
      if (sessionId) {
        localStorage.setItem('stripe_session_id', sessionId)
        localStorage.setItem('stripe_session_timestamp', Date.now().toString())
      }
      window.history.replaceState({}, '', window.location.pathname)
    } else if (checkoutStatus === 'cancel') {
      setSubscriptionSuccessMessage(null)
      setShowSubscriptionModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    } else if (portalStatus === 'return') {
      setSubscriptionSuccessMessage('Subscription updated successfully')
      setShowSubscriptionModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])
  
  // Handlers for opening windows from nav
  const handleOpenWindow = useCallback((type: string) => {
    const windowConfigs: Record<string, { emoji: string; titleKey: string }> = {
      chat: { emoji: '💬', titleKey: 'navigation.chat' },
      fileExplorer: { emoji: '📁', titleKey: 'navigation.fileExplorer' },
      settings: { emoji: '⚙️', titleKey: 'navigation.settings' },
      profile: { emoji: '👤', titleKey: 'navigation.profile' },
      representation: { emoji: '🧠', titleKey: 'navigation.representation' },
    }
    
    const config = windowConfigs[type]
    if (config) {
      let title = t(config.titleKey)
      if (type === 'chat') {
        const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
        title = `${t('navigation.chat')}${identityPart}`
      }
      openWindow(type as MobileWindow['contentType'], config.emoji, title)
    }
  }, [openWindow, t, i18n.language, selectedIdentity])
  
  // Login handlers
  const handleLoginClick = useCallback(() => setShowLoginModal(prev => !prev), [])
  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false)
    clearGuestSession()
    window.location.reload()
  }, [])
  const handleCloseLoginModal = useCallback(() => setShowLoginModal(false), [])
  
  // Usage limit handlers
  const handleUsageLimitReached = useCallback((resetTime: string) => {
    setUsageLimitResetTime(resetTime)
    setShowUsageLimitBanner(true)
  }, [])
  const handleOpenSubscription = useCallback(() => setShowSubscriptionModal(true), [])
  const handleUsageLimitBannerClose = useCallback(() => setShowUsageLimitBanner(false), [])
  
  // Guest mode handler
  const handleGuestModeStateChange = useCallback((guestMode: boolean) => setIsGuestMode(guestMode), [])
  
  // Calculate base carousel offset (percentage-based)
  const windowCount = Math.max(windows.length, 1)
  const baseOffsetPercent = -activeIndex * (100 / windowCount)
  
  // Sync carousel position when activeIndex changes
  // This resets the swipe offset to 0 BEFORE the render, ensuring clean transitions
  useLayoutEffect(() => {
    const el = carouselRef.current
    if (el) {
      // Reset swipe offset when activeIndex changes
      el.style.setProperty('--swipe-offset', '0px')
    }
  }, [activeIndex])
  
  // Render window content
  const renderWindowContent = (window: MobileWindow, windowIndex: number) => {
    const isActiveWindow = windowIndex === activeIndex
    switch (window.contentType) {
      case 'chat':
        return (
          <ChatApplication
            fontMode={fontMode === 'system' ? 'departure-mono' : fontMode}
            onGuestModeStateChange={handleGuestModeStateChange}
            onUsageLimitReached={handleUsageLimitReached}
            onOpenSubscription={handleOpenSubscription}
          />
        )
      case 'fileExplorer':
        return (
          <FileExplorer
            onActivateWindow={() => {}}
            onIdentitySelect={() => {
              // Open chat window and focus on it when identity is selected
              handleOpenWindow('chat')
            }}
          />
        )
      case 'settings':
        return <Settings />
      case 'profile':
        return <Profile />
      case 'representation':
        return <Representation isActive={isActiveWindow} />
      case 'auth':
        return <AuthWindow onAuthSuccess={() => {}} />
      default:
        return null
    }
  }
  
  return (
    <div
      style={{
        width: '100vw',
        // Fix iOS Safari 100vh bug: use dvh with vh fallback
        height: '100dvh',
        minHeight: '-webkit-fill-available',
        backgroundColor: colors.primaryBg,
        fontFamily,
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Prevent body-level overscroll on Safari
        overscrollBehavior: 'none',
        // Prevent double-tap zoom
        touchAction: 'manipulation',
      }}
    >
      <StippledBackground />
      
      {/* Background purchase executor */}
      {user && <MarketplacePurchaseExecutor />}
      
      {/* Mobile Navigation */}
      <MobileNav
        windows={windows.map((w, i) => ({
          id: w.id,
          title: w.title,
          contentType: w.contentType,
          isActive: i === activeIndex,
        }))}
        activeWindowIndex={activeIndex}
        onWindowSelect={setActiveIndex}
        onOpenWindow={handleOpenWindow}
        onCloseWindow={closeWindow}
        onThemeToggle={toggleTheme}
        isGuestMode={!user || isGuestMode}
        onLoginClick={handleLoginClick}
        showLoginModal={showLoginModal}
        onLoginSuccess={handleLoginSuccess}
        onCloseLoginModal={handleCloseLoginModal}
        fontMode={fontMode}
      />
      
      {/* Window Carousel */}
      <div
        ref={(el) => {
          carouselRef.current = el
          // Also set the swipe ref for native event listeners
          if (swipeRef) {
            (swipeRef as React.MutableRefObject<HTMLDivElement | null>).current = el
          }
        }}
        style={{
          position: 'absolute',
          top: 'calc(44px + env(safe-area-inset-top))',
          left: 0,
          right: 0,
          bottom: 'calc(60px + env(safe-area-inset-bottom))',
          overflow: 'hidden',
          touchAction: 'none',
          overscrollBehavior: 'none',
          ['--swipe-offset' as string]: '0px',
        }}
      >
        <div
          style={{
            display: 'flex',
            width: `${windowCount * 100}%`,
            height: '100%',
            transform: `translate3d(calc(${baseOffsetPercent}% + var(--swipe-offset, 0px)), 0, 0)`,
            // Explicit transition - 0.5s with iOS-style spring easing
            transition: 'transform 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
            willChange: 'transform',
          }}
        >
          {windows.map((window, index) => (
            <div
              key={window.id}
              style={{
                width: `${100 / windowCount}%`,
                height: '100%',
                flexShrink: 0,
                backgroundColor: colors.windowBg,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                // Allow vertical touch scrolling inside each window
                touchAction: 'pan-y',
                // Prevent overscroll within each window
                overscrollBehavior: 'contain',
              }}
            >
              {renderWindowContent(window, index)}
            </div>
          ))}
        </div>
      </div>
      
      {/* Empty state */}
      {windows.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: colors.secondaryText,
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💬')}</div>
          <div style={{ fontSize: '14px' }}>Tap an icon below to get started</div>
        </div>
      )}
      
      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <Subscription
          onClose={() => {
            setShowSubscriptionModal(false)
            setSubscriptionSuccessMessage(null)
          }}
          initialSuccessMessage={subscriptionSuccessMessage}
        />
      )}
      
      {/* Cookie consent */}
      <CookieConsent />
      
      {/* Usage limit banner */}
      <UsageLimitBanner
        show={showUsageLimitBanner}
        resetTime={usageLimitResetTime}
        onClose={handleUsageLimitBannerClose}
      />
    </div>
  )
}

